import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Smartphone, Monitor, Video, PenTool, X, ExternalLink, Maximize2, Briefcase } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Portfolio = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Initialize state from localStorage if available, otherwise default to 'all'
  const [activeTab, setActiveTab] = useState(() => {
    return localStorage.getItem('portfolioActiveTab') || 'all';
  });
  
  const [selectedItem, setSelectedItem] = useState(null); // State for the pop-up modal

  // Save to localStorage whenever activeTab changes
  useEffect(() => {
    localStorage.setItem('portfolioActiveTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    if (location.state && location.state.activeTab) {
      const incomingTab = location.state.activeTab;
      
      const tabMapping = {
        'Web Dev': 'web',
        'Video Editing': 'video',
        'Social Media': 'social',
        'Graphic Design': 'graphics',
        'Branding': 'branding'
      };

      if (tabMapping[incomingTab]) {
        setActiveTab(tabMapping[incomingTab]);
        // Clear the state so a page refresh doesn't revert to the incoming link 
        // instead of the user's last selected tab.
        navigate(location.pathname, { replace: true, state: {} });
      }
    }
  }, [location, navigate]);

  const portfolioItems = [
    {
      id: 1,
      category: 'graphics',
      title: 'ODUCCP',
      type: 'image',
      src: '/sample-design-1.jpg'
    },
    {
      id: 2,
      category: 'graphics',
      title: 'Vocal Factory Global',
      type: 'image',
      src: '/sample-design-2.jpg'
    },
    {
      id: 3,
      category: 'graphics',
      title: 'Esther Hayford - Praise Room',
      type: 'image',
      src: '/sample-design-3.jpg'
    },
    {
      id: 4,
      category: 'graphics',
      title: 'Nutridew Beverages',
      type: 'image',
      src: '/sample-design-4.jpg'
    },
    {
      id: 5,
      category: 'graphics',
      title: 'ODUCCP',
      type: 'image',
      src: '/sample-design-5.jpg'
    },
    {
      id: 6,
      category: 'graphics',
      title: 'Ghana Academy of Christian Counsellors',
      type: 'image',
      src: '/sample-design-6.jpg'
    },
    {
      id: 7,
      category: 'graphics',
      title: 'MyReach Alberta Ltd',
      type: 'image',
      src: '/sample-design-7.jpg'
    },
    {
      id: 8,
      category: 'graphics',
      title: 'Living Fountain International Ministries',
      type: 'image',
      src: '/sample-design-8.jpg'
    },
    {
      id: 9,
      category: 'video',
      title: 'Soul Of Akan || African Twi Highlife Medley',
      type: 'video',
      src: '/sample-video-9.mp4',
      url: 'https://youtu.be/bUBCBm3PSLk?si=d7LTi_h65eOZ3cR2'
    },
    {
      id: 10,
      category: 'video',
      title: 'Praise Room',
      type: 'video',
      src: '/sample-video-10.mp4',
      url: 'https://youtu.be/B8tAXjd8Wc8?si=tGDK9oJp_cZJAsml'
    },
    {
      id: 11,
      category: 'web',
      title: 'Ghana Academy Of Christian Counsellors',
      type: 'video',
      src: '/gacc.mp4',
      url: 'https://gaccacademy.com'
    },
    {
      id: 12,
      category: 'web',
      title: 'ODUCCP Journal',
      type: 'video',
      src: '/ODUCCP.mp4',
      url: 'https://journal.oduccp.edu.gh'
    },
    {
      id: 13,
      category: 'social',
      title: 'Vocal Factory Global - Instagram',
      type: 'video',
      src: '/sample-video-13.mp4',
      url: 'https://www.instagram.com/vocalfactoryglobal?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw=='
    },
    {
      id: 14,
      category: 'video',
      title: 'Nigerian Highlife Medley feat Lady Laurel',
      type: 'video',
      src: '/sample-video-11.mp4',
      url: 'https://youtu.be/yfhq_HW9aBs?si=ExLQgrzG4XqQw9lP'
    },
    {
      id: 15,
      category: 'video',
      title: 'African Praise Medley',
      type: 'video',
      src: '/sample-video-12.mp4',
      url: 'https://youtu.be/sYKlr373KV0?si=saOL40c_EuM4bANy'
    },
    // --- BRANDING ITEMS ---
    {
      id: 16,
      category: 'branding',
      title: 'ValuVant Solutions (LOGO)',
      type: 'image',
      src: '/branding-1.jpg' // Assumed filename
    },
    {
      id: 17,
      category: 'branding',
      title: 'Vocal Factory Global (LOGO)',
      type: 'image',
      src: '/branding-2.jpg' // Assumed filename
    },
    {
      id: 18,
      category: 'branding',
      title: 'Cremlora LLC (LOGO)',
      type: 'image',
      src: '/branding-3.jpg' // Assumed filename
    },
    // --- NEW WEB DEV ITEMS ---
    {
      id: 19,
      category: 'web',
      title: 'Vocal Factory Global',
      type: 'video',
      src: '/vocalfactory.mp4', // Ensure this file exists in public folder
      url: 'https://vocalfactoryglobal.com'
    },
    {
      id: 20,
      category: 'web',
      title: 'Cremlora LLC',
      type: 'video',
      src: '/cremlora.mp4', // Ensure this file exists in public folder
      url: 'https://cremlorallc.com'
    }
  ];

  const filteredItems = activeTab === 'all' 
    ? portfolioItems 
    : portfolioItems.filter(item => item.category === activeTab);

  // Helper to render content inside the modal
  const renderModalContent = (item) => {
    switch (item.type) {
      case 'image':
        return (
            <div style={{ textAlign: 'center' }}>
                <img src={item.src} alt={item.title} style={{ width: '100%', height: 'auto', borderRadius: '8px', marginBottom: '1rem' }} />
                {item.url && (
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', marginTop: '10px' }}>
                        Visit Website <ExternalLink size={18} />
                    </a>
                )}
            </div>
        );
      case 'video':
        return (
          <div style={{ textAlign: 'center' }}>
            <video controls autoPlay style={{ width: '100%', borderRadius: '8px', marginBottom: '1rem' }}>
              <source src={item.src} type="video/mp4" />
              Your browser does not support the video tag.
            </video>
            {item.url && (
                <a href={item.url} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', marginTop: '10px' }}>
                    {/* Show "Visit Link" for video OR social category items, otherwise "Visit Website" */}
                    {(item.category === 'video' || item.category === 'social') ? 'Visit Link' : 'Visit Website'} <ExternalLink size={18} />
                </a>
            )}
          </div>
        );
      case 'link':
        return (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Monitor size={80} style={{ color: 'var(--primary-gold)', marginBottom: '1rem' }} />
            <h2 style={{ marginBottom: '1rem' }}>{item.title}</h2>
            <a href={item.url} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
              Visit Website <ExternalLink size={18} />
            </a>
          </div>
        );
      case 'card':
        return (
          <div style={{ textAlign: 'center', padding: '2rem' }}>
            <Smartphone size={80} style={{ color: 'var(--primary-red)', marginBottom: '1rem' }} />
            <h2 style={{ marginBottom: '1rem' }}>{item.title}</h2>
            {item.url && (
                <a href={item.url} target="_blank" rel="noopener noreferrer" className="btn btn-primary" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem' }}>
                  Visit Link <ExternalLink size={18} />
                </a>
            )}
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <motion.div 
      className="section"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container">
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <h1 className="text-red">Capabilities</h1>
          <p>Explore our recent projects across different mediums.</p>
        </div>

        {/* Category Filter Buttons */}
        <div className="portfolio-tabs" style={{ display: 'flex', justifyContent: 'center', gap: '1rem', flexWrap: 'wrap', marginBottom: '2rem' }}>
          <button onClick={() => setActiveTab('all')} className={`btn ${activeTab === 'all' ? 'active-tab' : ''}`} style={btnStyle(activeTab === 'all')}>All</button>
          <button onClick={() => setActiveTab('branding')} className={`btn ${activeTab === 'branding' ? 'active-tab' : ''}`} style={btnStyle(activeTab === 'branding')}><Briefcase size={16} /> Branding</button>
          <button onClick={() => setActiveTab('social')} className={`btn ${activeTab === 'social' ? 'active-tab' : ''}`} style={btnStyle(activeTab === 'social')}><Smartphone size={16} /> Social Media</button>
          <button onClick={() => setActiveTab('graphics')} className={`btn ${activeTab === 'graphics' ? 'active-tab' : ''}`} style={btnStyle(activeTab === 'graphics')}><PenTool size={16} /> Graphic Design</button>
          <button onClick={() => setActiveTab('video')} className={`btn ${activeTab === 'video' ? 'active-tab' : ''}`} style={btnStyle(activeTab === 'video')}><Video size={16} /> Visual Storytelling</button>
          <button onClick={() => setActiveTab('web')} className={`btn ${activeTab === 'web' ? 'active-tab' : ''}`} style={btnStyle(activeTab === 'web')}><Monitor size={16} /> Web Dev</button>
        </div>

        {/* Grid Display with Layout Animations - RESIZED */}
        <motion.div 
          className="portfolio-grid" 
          layout
          style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1.5rem' }}
        >
          <AnimatePresence mode='popLayout'>
            {filteredItems.map(item => (
              <motion.div 
                layout
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.3 }}
                key={item.id} 
                className="portfolio-card" 
                style={{ border: '1px solid #eee', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', cursor: 'pointer' }}
                onClick={() => setSelectedItem(item)}
                whileHover={{ y: -5, boxShadow: '0 8px 15px rgba(0,0,0,0.15)' }}
              >
                {/* Media Rendering - RESIZED HEIGHT */}
                {item.type === 'image' && (
                  <div style={{ height: '160px', overflow: 'hidden', position: 'relative' }}>
                    <img src={item.src} alt={item.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  </div>
                )}

                {item.type === 'video' && (
                  <div style={{ height: '160px', background: '#000', position: 'relative' }}>
                    <video 
                      autoPlay 
                      loop 
                      muted 
                      playsInline 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    >
                      <source src={item.src} type="video/mp4" />
                    </video>
                    {/* Video icon removed for all video types */}
                  </div>
                )}

                {item.type === 'link' && (
                  <div style={{ height: '160px', background: 'var(--primary-gold, #d4af37)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                    <Monitor size={48} />
                  </div>
                )}

                {item.type === 'card' && (
                  <div style={{ height: '160px', background: 'var(--primary-red, #c41e3a)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                    <Smartphone size={48} />
                  </div>
                )}

                {/* Content - RESIZED PADDING */}
                <div className="p-content" style={{ padding: '1rem' }}>
                  <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>{item.title}</h3>
                  
                  {/* Updated Text Logic: If URL exists, show 'Visit Link' for video/social, 'Visit Website' for others, else 'Click to Preview' */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'var(--primary-red)', fontWeight: '600', fontSize: '0.8rem', marginTop: '0.5rem' }}>
                    {item.url ? (
                      <>
                        <ExternalLink size={14} /> {(item.category === 'video' || item.category === 'social') ? 'Visit Link' : 'Visit Website'}
                      </>
                    ) : (
                      <>
                        <Maximize2 size={14} /> Click to Preview
                      </>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>

      {/* Pop-up Modal */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
              background: 'rgba(0,0,0,0.85)', zIndex: 2000,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              padding: '2rem'
            }}
            onClick={() => setSelectedItem(null)}
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              style={{
                background: 'white',
                padding: '20px',
                borderRadius: '12px',
                width: '100%',
                maxWidth: '900px',
                maxHeight: '90vh',
                overflowY: 'auto',
                position: 'relative'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button 
                onClick={() => setSelectedItem(null)}
                style={{
                  position: 'absolute', top: '15px', right: '15px',
                  background: '#f0f0f0', border: 'none', borderRadius: '50%',
                  width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                  cursor: 'pointer'
                }}
              >
                <X size={20} />
              </button>

              <h2 style={{ marginBottom: '1rem', paddingRight: '40px' }}>{selectedItem.title}</h2>
              
              {renderModalContent(selectedItem)}

            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const btnStyle = (isActive) => ({
  padding: '0.5rem 1.5rem',
  border: '1px solid #ddd',
  borderRadius: '20px',
  background: isActive ? '#333' : 'white',
  color: isActive ? 'white' : '#333',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  transition: 'all 0.3s ease'
});

export default Portfolio;